import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import "./BillingHistory.css";
import { getBillingHistory } from "../../../../services/api/app/subscriptions";
import { plans } from "../../../../data/pricingModal";
import logger from "../../../../utils/logger";

const statusClass = (status) => {
  const key = String(status || "").toLowerCase();
  if (key === "paid" || key === "active") return "paid";
  if (key === "failed" || key === "expired") return "failed";
  if (key === "pending") return "pending";
  return "";
};

const extractCredits = (entry) => {
  const fromField = Number(entry?.credits);
  if (Number.isFinite(fromField) && fromField >= 0) return fromField;

  const raw = String(entry?.plan || "");
  const match = raw.match(/(\d[\d,]*)\s*credits?/i);
  if (!match) return null;
  const parsed = Number(String(match[1]).replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : null;
};

/** Map subscription credit allotments to Lite / Pro / Max from the pricing catalog. */
const mapSubscriptionPlanName = (credits, rawName = "") => {
  const named = String(rawName).match(/\b(lite|pro|max)\b/i);
  if (named) {
    const key = named[1].toLowerCase();
    return key.charAt(0).toUpperCase() + key.slice(1);
  }

  const count = Number(credits);
  if (!Number.isFinite(count)) return "Subscription";

  for (const plan of plans) {
    const tierCredits = [
      Number(plan.credits),
      ...(plan.creditTiers || []).map((tier) => Number(tier.credits)),
    ].filter((value) => Number.isFinite(value));
    if (tierCredits.includes(count)) return plan.name;
  }

  const liteCap = Math.max(
    0,
    ...((plans.find((plan) => plan.name === "Lite")?.creditTiers || []).map(
      (tier) => Number(tier.credits)
    ) || [200])
  );
  const proCap = Math.max(
    0,
    ...((plans.find((plan) => plan.name === "Pro")?.creditTiers || []).map(
      (tier) => Number(tier.credits)
    ) || [2500])
  );

  if (count <= liteCap) return "Lite";
  if (count <= proCap) return "Pro";
  return "Max";
};

/**
 * Single-line plan display:
 * - Credit packs → "Pay as you go"
 * - Subscriptions → Lite / Pro / Max
 * Credits are returned separately so the UI can highlight them.
 */
const resolvePlanDisplay = (entry) => {
  const type = String(entry?.type || "").toLowerCase();
  const raw = String(entry?.plan || "")
    .replace(/-/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const credits = extractCredits(entry);

  const looksMonthly = /\bmonthly\b|\/\s*mo\b|per\s*month/i.test(raw);
  const looksYearly = /\byearly\b|\bannual\b|\/\s*yr\b|per\s*year/i.test(raw);
  const looksCreditPackOnly = /^\d[\d,]*\s*credits?\s*$/i.test(raw);
  const explicitPayg = /pay\s*as\s*you\s*go/i.test(raw);

  const isPayg =
    type === "credit" ||
    explicitPayg ||
    (looksCreditPackOnly && type !== "subscription" && !looksMonthly && !looksYearly);

  const creditsLabel =
    credits == null
      ? null
      : `${credits.toLocaleString()} credit${credits === 1 ? "" : "s"}`;

  if (isPayg) {
    return { planName: "Pay as you go", creditsLabel };
  }

  return {
    planName: mapSubscriptionPlanName(credits, raw),
    creditsLabel,
  };
};

const BillingHistory = () => {
  const { getToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const token = await getToken();
      if (!token) {
        setHistory([]);
        setError("Sign in to view billing history.");
        return;
      }

      const response = await getBillingHistory(token);
      if (!response?.success) {
        throw new Error(response?.message || "Failed to load billing history");
      }

      setHistory(Array.isArray(response.history) ? response.history : []);
    } catch (err) {
      logger.error("Failed to load billing history", err);
      setHistory([]);
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          "Couldn’t load billing history."
      );
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const headerStatus = loading
    ? "Loading billing history…"
    : error
      ? error
      : history.length === 0
        ? "No purchases yet"
        : `${history.length} record${history.length === 1 ? "" : "s"}`;

  const headerStatusTone = error ? "error" : "muted";

  return (
    <div className="billing-container">
      <div className="billing-heading">
        <h2 className="billing-title">Billing</h2>
        <p
          className={`billing-header-status billing-header-status--${headerStatusTone}`}
          role="status"
          aria-live="polite"
        >
          {headerStatus || "\u00A0"}
        </p>
      </div>

      <div
        className={`billing-table-wrapper${loading ? " is-loading" : ""}`}
        aria-busy={loading}
      >
        {loading && history.length === 0 ? (
          <div className="billing-empty">Fetching your invoices…</div>
        ) : !loading && history.length === 0 ? (
          <div className="billing-empty">
            <p className="billing-empty__title">No billing history yet</p>
            <p className="billing-empty__copy">
              Credit packs and subscription charges will show up here after you
              make a purchase.
            </p>
          </div>
        ) : (
          <table className="billing-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Amount</th>
                <th>Plan</th>
                <th>Status</th>
                <th>Invoice</th>
              </tr>
            </thead>
            <tbody>
              {history.map((entry, index) => {
                const tone = statusClass(entry.status);
                const invoiceUrl = entry.invoice_url || entry.invoiceUrl;
                const { planName, creditsLabel } = resolvePlanDisplay(entry);
                return (
                  <tr
                    className="billing-row"
                    key={`${entry.date}-${planName}-${entry.amount}-${index}`}
                  >
                    <td>{entry.date || "—"}</td>
                    <td>{entry.amount || "—"}</td>
                    <td>
                      <div className="billing-plan-cell">
                        <span className="billing-plan-name">{planName}</span>
                        {creditsLabel && (
                          <>
                            <span className="billing-plan-sep" aria-hidden="true">
                              ·
                            </span>
                            <span className="billing-plan-credits">
                              {creditsLabel}
                            </span>
                          </>
                        )}
                      </div>
                    </td>
                    <td className={`status${tone ? ` ${tone}` : ""}`}>
                      {entry.status || "—"}
                    </td>
                    <td>
                      {invoiceUrl ? (
                        <a
                          href={invoiceUrl}
                          className="billing-invoice-link"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Download
                        </a>
                      ) : (
                        <span className="billing-invoice-na">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default BillingHistory;
